import {
  createUnifiedTheme,
  defaultComponentThemes,
  pageTheme as defaultPageThemes,
  palettes,
  type PageTheme,
  type UnifiedTheme,
} from '@backstage/theme';
import {
  FONT_FAMILY,
  RADIUS,
  TOKENS,
  type Mode,
  type ModeTokens,
} from './tokens';

type Components = NonNullable<typeof defaultComponentThemes>;
type StyleSlot = unknown;

/** Merges two styleOverrides slots, each an object or a function of the theme props. */
const mergeSlot = (base: StyleSlot, extra: StyleSlot): StyleSlot => {
  if (base === undefined) return extra;
  const resolve = (slot: StyleSlot, props: unknown) =>
    typeof slot === 'function' ? slot(props) : slot;
  return (props: unknown) => ({
    ...(resolve(base, props) as object),
    ...(resolve(extra, props) as object),
  });
};

/** Adds our overrides on top of Backstage's default component themes instead of replacing them. */
const mergeComponents = (base: Components, extra: Record<string, any>) => {
  const result: Record<string, any> = { ...base };
  for (const [name, value] of Object.entries(extra)) {
    const current = (base as Record<string, any>)[name] ?? {};
    const styleOverrides: Record<string, StyleSlot> = {
      ...(typeof current.styleOverrides === 'object'
        ? current.styleOverrides
        : {}),
    };
    for (const [slot, style] of Object.entries(value.styleOverrides ?? {})) {
      styleOverrides[slot] = value.replace?.includes(slot)
        ? style
        : mergeSlot(styleOverrides[slot], style);
    }
    result[name] = {
      ...current,
      defaultProps: { ...current.defaultProps, ...value.defaultProps },
      styleOverrides,
    };
  }
  return result as Components;
};

/** Flat page headers (no Backstage wave banner), for every page theme id. */
const flatPageThemes = (t: ModeTokens): Record<string, PageTheme> =>
  Object.fromEntries(
    Object.keys(defaultPageThemes).map(id => [
      id,
      {
        colors: [t.surface, t.surface],
        shape: 'none',
        backgroundImage: 'none',
        fontColor: t.text,
      },
    ]),
  );

const componentOverrides = (t: ModeTokens) => ({
  MuiPaper: {
    styleOverrides: {
      rounded: { borderRadius: RADIUS },
      elevation1: { boxShadow: 'none', border: `1px solid ${t.border}` },
      elevation2: { boxShadow: 'none', border: `1px solid ${t.border}` },
      elevation3: { boxShadow: 'none', border: `1px solid ${t.border}` },
      elevation8: {
        border: `1px solid ${t.border}`,
        boxShadow:
          '0 12px 24px -4px rgba(16, 24, 40, 0.10), 0 4px 8px -2px rgba(16, 24, 40, 0.06)',
      },
    },
  },
  MuiCardHeader: {
    styleOverrides: {
      root: { padding: '16px 20px 12px' },
      title: { fontSize: 16, fontWeight: 600, lineHeight: 1.4 },
      subheader: { fontSize: 13, color: t.textSecondary },
    },
  },
  MuiCardContent: {
    styleOverrides: { root: { padding: '12px 20px 20px' } },
  },
  MuiDivider: {
    styleOverrides: { root: { backgroundColor: t.border } },
  },
  MuiButton: {
    defaultProps: { disableElevation: true },
    styleOverrides: {
      root: {
        textTransform: 'none',
        borderRadius: RADIUS,
        fontWeight: 500,
        letterSpacing: 0,
        minHeight: 36,
      },
      containedPrimary: {
        color: t.onBrand,
        '&:hover': { backgroundColor: t.brandHover },
      },
      outlined: { borderColor: t.border, backgroundColor: t.surface },
      outlinedPrimary: { borderColor: t.brand },
    },
  },
  // MUI v4 form controls default to the secondary (yellow) color: keep them teal.
  MuiCheckbox: { defaultProps: { color: 'primary' } },
  MuiRadio: { defaultProps: { color: 'primary' } },
  MuiSwitch: { defaultProps: { color: 'primary' } },
  MuiBadge: {
    styleOverrides: { colorSecondary: { color: t.onAccent, fontWeight: 600 } },
  },
  MuiIconButton: {
    styleOverrides: { root: { borderRadius: RADIUS } },
  },
  MuiTabs: {
    styleOverrides: {
      indicator: { height: 2, backgroundColor: t.brand },
    },
  },
  MuiTab: {
    replace: ['root', 'textColorPrimary'],
    styleOverrides: {
      root: {
        textTransform: 'none',
        letterSpacing: 0,
        fontWeight: 500,
        fontSize: 14,
        minWidth: 'auto !important',
        minHeight: 40,
        padding: '8px 12px',
        color: t.textSecondary,
        '&:hover': { color: t.text },
        '&$selected': { color: t.brand, fontWeight: 600 },
      },
      textColorPrimary: {
        color: t.textSecondary,
        '&$selected': { color: t.brand, fontWeight: 600 },
      },
      textColorInherit: { opacity: 1, color: t.textSecondary },
    },
  },
  MuiTableRow: {
    replace: ['root', 'head'],
    styleOverrides: {
      root: {},
      head: {},
      hover: {
        '&:hover': { backgroundColor: `${t.surfaceSubtle} !important` },
      },
    },
  },
  MuiTableCell: {
    replace: ['root', 'sizeSmall', 'head'],
    styleOverrides: {
      root: {
        wordBreak: 'break-word',
        overflow: 'hidden',
        verticalAlign: 'middle',
        lineHeight: 1.4,
        padding: '12px 16px',
        fontSize: 14,
        borderBottom: `1px solid ${t.neutralSoft}`,
      },
      sizeSmall: { padding: '8px 16px' },
      head: {
        backgroundColor: t.surface,
        color: t.text,
        fontSize: 14,
        fontWeight: 500,
        borderBottom: `1px solid ${t.border}`,
        lineHeight: 1.4,
        whiteSpace: 'nowrap',
      },
    },
  },
  MuiChip: {
    styleOverrides: {
      root: {
        borderRadius: 6,
        backgroundColor: t.neutralSoft,
        color: t.text,
      },
      sizeSmall: { height: 22 },
      outlined: { borderColor: t.border },
    },
  },
  MuiOutlinedInput: {
    styleOverrides: {
      root: {
        backgroundColor: t.surface,
        borderRadius: RADIUS,
        '&:hover $notchedOutline': { borderColor: t.borderStrong },
        '&$focused $notchedOutline': { borderColor: t.brand },
      },
      notchedOutline: { borderColor: t.border },
    },
  },
  MuiTooltip: {
    styleOverrides: {
      tooltip: {
        backgroundColor: t.text,
        color: t.surface,
        fontSize: 12,
        borderRadius: 6,
        padding: '6px 8px',
      },
    },
  },
  MuiDrawer: {
    styleOverrides: { paper: { borderLeft: `1px solid ${t.border}` } },
  },
  MuiDialog: {
    styleOverrides: { paper: { borderRadius: 12 } },
  },

  // Backstage components
  BackstageHeader: {
    styleOverrides: {
      header: {
        backgroundColor: t.surface,
        boxShadow: 'none',
        borderTop: `3px solid ${t.accent}`,
        borderBottom: `1px solid ${t.border}`,
        padding: '20px 24px',
      },
      title: { fontSize: 24, fontWeight: 600, letterSpacing: '-0.01em' },
      subtitle: {
        color: t.textSecondary,
        opacity: 1,
        fontSize: 14,
        marginTop: 4,
      },
      type: {
        color: t.textMuted,
        opacity: 1,
        fontWeight: 500,
        letterSpacing: '0.04em',
      },
      breadcrumb: { color: t.textSecondary },
    },
  },
  BackstageHeaderLabel: {
    styleOverrides: {
      label: { color: t.textMuted },
      value: { color: t.text },
    },
  },
  BackstageHeaderTabs: {
    styleOverrides: {
      tabsWrapper: {
        backgroundColor: t.surface,
        borderBottom: `1px solid ${t.border}`,
        paddingLeft: 12,
      },
      defaultTab: {
        textTransform: 'none',
        fontSize: 14,
        fontWeight: 500,
        letterSpacing: 0,
        padding: '12px 12px',
        color: t.textSecondary,
      },
      selected: { color: `${t.brand} !important`, fontWeight: 600 },
      tabRoot: { '&:hover': { backgroundColor: 'transparent', color: t.text } },
    },
  },
  BackstageContent: {
    styleOverrides: { root: { backgroundColor: t.app } },
  },
  BackstageContentHeader: {
    styleOverrides: {
      title: { fontSize: 18, fontWeight: 600 },
    },
  },
  BackstageInfoCardCardActionsTopRight: {
    styleOverrides: { root: { top: 12, right: 12 } },
  },
  // Bobbee sidebar: teal gradient, white items, selected item as a darker rounded pill.
  BackstageSidebar: {
    styleOverrides: {
      drawer: {
        background: `linear-gradient(180deg, ${t.sidebarTop} 0%, ${t.sidebarBottom} 100%)`,
        borderRight: 'none',
      },
    },
  },
  BackstageSidebarItem: {
    // Deep-merged into Backstage's own item styles by MUI: neutralize its full-width
    // backgrounds and left indicator, the pill below replaces them.
    styleOverrides: {
      root: {
        height: 42,
        position: 'relative',
        color: t.sidebarTextMuted,
        // The pill is drawn by ::before so it keeps a margin inside the fixed-width drawer.
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: '2px 12px',
          borderRadius: RADIUS,
          backgroundColor: 'transparent',
          transition: 'background-color 120ms ease',
        },
        '& > *': { position: 'relative' },
      },
      label: { fontWeight: 500, fontSize: 14 },
      highlightable: {
        '&:hover': { color: t.sidebarText, background: 'none' },
        '&:hover::before': { backgroundColor: t.sidebarHover },
      },
      highlighted: {
        color: t.sidebarText,
        background: 'none',
        '&::before': { backgroundColor: t.sidebarHover },
      },
      selected: {
        '&$root': { color: t.sidebarText, borderLeft: 'none' },
        '&$root::before': { backgroundColor: t.sidebarSelected },
        '& $closedItemIcon': { paddingRight: 0 },
        '& $iconContainer': { marginLeft: 0, color: t.accent },
      },
    },
  },
  BackstageSidebarSubmenu: {
    styleOverrides: {
      drawer: {
        background: t.sidebarBottom,
        borderRight: 'none',
        boxShadow: 'none',
        color: t.sidebarText,
      },
    },
  },
  BackstageTableToolbar: {
    styleOverrides: {
      root: { padding: '12px 16px' },
      title: { fontWeight: 600 },
    },
  },
  BackstageTable: {
    styleOverrides: { root: { overflow: 'hidden' } },
  },
});

const createBobbeeTheme = (mode: Mode): UnifiedTheme => {
  const t = TOKENS[mode];
  const base = palettes[mode];
  return createUnifiedTheme({
    palette: {
      ...base,
      mode,
      type: mode,
      primary: { main: t.brand, dark: t.brandHover, contrastText: t.onBrand },
      secondary: {
        main: t.accent,
        dark: t.accentHover,
        contrastText: t.onAccent,
      },
      success: { main: t.success },
      warning: { main: t.warning },
      error: { main: t.error },
      info: { main: t.info },
      background: { default: t.app, paper: t.surface },
      text: { primary: t.text, secondary: t.textSecondary },
      divider: t.border,
      border: t.border,
      textContrast: t.text,
      textSubtle: t.textSecondary,
      textVerySubtle: t.borderStrong,
      highlight: t.brandSoft,
      link: t.brand,
      linkHover: t.brandHover,
      status: {
        ok: t.success,
        warning: t.warning,
        error: t.error,
        pending: t.neutral,
        running: t.info,
        aborted: t.neutral,
      },
      errorBackground: t.errorSoft,
      warningBackground: t.warningSoft,
      infoBackground: t.infoSoft,
      errorText: t.error,
      warningText: t.warning,
      infoText: t.info,
      banner: {
        ...base.banner,
        info: t.info,
        error: t.error,
        warning: t.warning,
      },
      navigation: {
        background: t.sidebarTop,
        indicator: 'transparent',
        color: t.sidebarTextMuted,
        selectedColor: t.sidebarText,
        navItem: { hoverBackground: t.sidebarHover },
        submenu: { background: t.sidebarBottom },
      },
    } as any,
    fontFamily: FONT_FAMILY,
    defaultPageTheme: 'home',
    pageTheme: flatPageThemes(t),
    components: {
      ...mergeComponents(defaultComponentThemes!, componentOverrides(t)),
      // Backstage's baseline is a function of the theme: extend it rather than merging slots.
      MuiCssBaseline: {
        styleOverrides: (theme: any) => {
          const baseline = defaultComponentThemes!.MuiCssBaseline!
            .styleOverrides as (theme: any) => Record<string, any>;
          const styles = baseline(theme);
          return {
            ...styles,
            body: {
              ...styles.body,
              backgroundColor: t.app,
              WebkitFontSmoothing: 'antialiased',
            },
            '::selection': { backgroundColor: t.accentSoft, color: t.text },
          };
        },
      },
    },
  });
};

export const bobbeeLightTheme = createBobbeeTheme('light');
export const bobbeeDarkTheme = createBobbeeTheme('dark');
